// Core
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Button, Chip, Crumbs, DateRangePopover, PageHead, Tabs } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";
import UploadHelpButton from "components/UploadHelpButton";

// Sub-views
import OpportunityCostsCharts from "pages/ManageOpportunityCosts/Charts";
import ListOpportunityCosts from "pages/ManageOpportunityCosts/List";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectTimeSelector, updateTimeSelector } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useAddNewOpportunityCostsMutation } from "state/opportunity_costs/opportunityCostsApiSlice";
import { useGetTaskStatusByIdQuery } from "state/tasks/tasksApiSlice";
import { selectActiveTasksByType } from "state/tasks/tasksSlice";

// Utils
import { handleFileUpload } from "utils/handlers";
import useTaskStatusEffect from "utils/hooks/useTaskStatusEffect";

const TASK_TYPE = "OPPORTUNITY_COSTS";
const HREF_TEMPLATE = "/docs/template_opportunity_cost.csv";

function ManageOpportunityCosts() {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["opportunity_costs"]);
  const { startDate, endDate, granularity, horizon, isExpanded } = useSelector(selectTimeSelector);

  const [tab, setTab] = useState("table");
  const [chartMeterId, setChartMeterId] = useState("");
  const fileInputRef = useRef(null);

  // CREATE Opportunity Costs (CSV upload + task-status polling)
  const activeTasks = useSelector(selectActiveTasksByType(TASK_TYPE));
  const [addOpportunityCosts] = useAddNewOpportunityCostsMutation();
  const handleUpload = handleFileUpload(addOpportunityCosts, community.id, dispatch, TASK_TYPE);

  const {
    data: taskStatus,
    isFetching,
    isSuccess,
  } = useGetTaskStatusByIdQuery(
    { taskId: activeTasks[0]?.id, communityId: community.id },
    {
      pollingInterval: 3000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
      skip: activeTasks.length === 0,
    }
  );
  useTaskStatusEffect({
    isSuccess,
    isFetching,
    taskStatus,
    activeLoadsTasks: activeTasks,
    dispatch,
    TASK_TYPE,
  });

  const isUploading = activeTasks.length > 0;

  const tabs = [
    { id: "table", icon: "table_rows", label: t("tabs.table") },
    { id: "charts", icon: "show_chart", label: t("tabs.charts") },
  ];

  const actions = (
    <div className="flex items-center gap-[10px] flex-wrap">
      {tab === "charts" && (
        <>
          <DateRangePopover
            value={{ start: startDate, end: endDate }}
            onChange={(v) =>
              dispatch(
                updateTimeSelector({
                  startDate: v.start,
                  endDate: v.end,
                  granularity,
                  horizon,
                  isExpanded,
                })
              )
            }
          />
          <span className="w-px self-stretch bg-border my-1 mx-[2px]" />
        </>
      )}
      {isUploading && (
        <Chip tone="amber">
          <Icon name="hourglass_top" style={{ fontSize: 13 }} />
          {t("actions.uploading", { defaultValue: "A carregar…", ns: "common" })}
        </Chip>
      )}
      <Button variant="ghost" onClick={() => window.open(HREF_TEMPLATE)}>
        <Icon name="download" />
        {t("actions.template", { defaultValue: "Template", ns: "common" })}
      </Button>
      <UploadHelpButton size="md" />
      <Button variant="ghost" disabled={isUploading} onClick={() => fileInputRef.current?.click()}>
        <Icon name="upload_file" />
        {t("actions.upload", { defaultValue: "Carregar", ns: "common" })}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleUpload}
      />
    </div>
  );

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="opportunity-costs-list" />
      <Crumbs items={[{ label: community?.name || "—" }, { label: t("title") }]} />
      <PageHead title={t("title")} subtitle={t("subtitle")} actions={actions} />

      <Tabs tabs={tabs} active={tab} onChange={setTab} />
      {tab === "table" && <ListOpportunityCosts />}
      {tab === "charts" && (
        <OpportunityCostsCharts meterId={chartMeterId} setMeterId={setChartMeterId} />
      )}
    </div>
  );
}

export default ManageOpportunityCosts;
